<script setup lang="ts">
import type { Fields, ImportField, ImportRecord, ImportResult, Message, ParentChoice, ParentSlot, Row, Section } from './types';
import axios from 'axios';
import { ChevronDown, ChevronRight, ChevronUp, CircleCheck, CircleMinus, CircleX, Download, FileSpreadsheet, Info, RefreshCw, TriangleAlert, Upload } from 'lucide';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphCallout } from '@/components/base/callout';
import { SphCheckbox } from '@/components/base/checkbox';
import { SphField } from '@/components/base/field';
import { SphIcon } from '@/components/base/icon';
import { SphInput } from '@/components/base/input';
import { SphLink } from '@/components/base/link';
import { SphSelect } from '@/components/base/select';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    title: string
    fields: Fields
    templateUrl: string | null
    verifyUrl: string
    reverifyUrl: string
    commitUrl: string
    childrenUrl: string
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const PARENT_SLOTS: { slot: ParentSlot, label: string }[] = [
    { slot: 'parent1', label: 'Bezugsperson 1' },
    { slot: 'parent2', label: 'Bezugsperson 2' },
];

const YES_NO = ['Ja', 'Nein'];

const HINTS = [
    'Pflichtangaben sind Vorname, Nachname, Geschlecht und Geburtsdatum des Kindes sowie mindestens eine Bezugsperson.',
    'Nach dem Hochladen wird jede Zeile geprüft. Fehler verhindern den Import, Warnungen und Hinweise nicht. Sie können jede Zeile aufklappen und korrigieren oder sie vom Import ausnehmen.',
    'Ein Kind, das mit gleichem Namen und Geburtsdatum bereits existiert, wird standardmäßig für den Import deaktiviert. Sie können diese Entscheidung aber überstimmen und das Kind trotzdem als neues Kind anlegen.',
    'Eine Bezugsperson, die mit gleichem Vor- und Nachnamen bereits im Kindergarten existiert, wird mit dem Kind verknüpft und nicht neu angelegt. Gibt es mehrere mit diesem Namen, wählen Sie in der Zeile aus, welche gemeint ist.',
    'Steht eine Bezugsperson mit gleichem Vor- und Nachnamen in mehreren Zeilen, etwa bei Geschwistern, wird sie nur einmal angelegt. Die Angaben zur Person stammen dann aus der vermerkten Zeile; Sorgerecht, Abholberechtigung und die Zahlungsdaten werden aber für das jeweilige Kind übernommen.',
    'Für jede Bezugsperson können Sie in der aufgeklappten Zeile festlegen, ob sie mit einer bestehenden Bezugsperson verknüpft, aus einer früheren Zeile übernommen, neu angelegt oder nicht importiert wird.',
    'Mit tatsächlichem Aufnahmedatum wird das Kind ab diesem Tag aktiv geführt, nur mit gewünschtem Aufnahmedatum kommt es auf die Warteliste, ohne Datum gilt es ab heute als aktiv. Gruppen, die es noch nicht in der Einrichtung gibt, werden angelegt.',
];

const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const rows = ref<Row[]>([]);
const pending = ref(false);
// Set by every edit: the import may only run on rows the server has verified as they are now.
const dirty = ref(false);
const error = ref('');
const missingColumns = ref<string[]>([]);
const result = ref<ImportResult | null>(null);
const expanded = ref<Set<number>>(new Set());
const explanationOpen = ref(false);
const onlyWithMessages = ref(false);

const includedRows = computed(() => rows.value.filter((row) => !row.excluded));
// An excluded row blocks nothing, also before the server has confirmed that.
const rowsWithErrors = computed(() => rows.value.filter((row) => !row.excluded && row.verification.hasErrors));
const rowsWithWarnings = computed(() => rows.value.filter((row) => !row.excluded && countByLevel(row, 'warning') > 0));
const canImport = computed(() => !pending.value && !dirty.value && includedRows.value.length > 0 && rowsWithErrors.value.length === 0);

const visibleRows = computed(() => {
    if (!onlyWithMessages.value) {
        return rows.value;
    }

    return rows.value.filter((row) => allMessages(row).some((message) => message.level !== 'info'));
});

// Applies to the rows on screen, so "only rows with errors" plus a click excludes exactly those.
const allVisibleIncluded = computed(() => visibleRows.value.every((row) => !row.excluded));
const importAllState = computed<boolean | 'indeterminate'>(() => {
    if (allVisibleIncluded.value) {
        return true;
    }

    return visibleRows.value.some((row) => !row.excluded) ? 'indeterminate' : false;
});

function toggleAll() {
    const include = !allVisibleIncluded.value;
    visibleRows.value.forEach((row) => row.excluded = !include);
    changeSelection();
}

function allMessages(row: Row): Message[] {
    const { child, parent1, parent2 } = row.verification.messages;

    return [...child, ...parent1, ...parent2];
}

function countByLevel(row: Row, level: Message['level']): number {
    return allMessages(row).filter((message) => message.level === level).length;
}

const MESSAGE_ICONS = { error: CircleX, warning: TriangleAlert, info: Info };
const LEVEL_ORDER: Message['level'][] = ['error', 'warning', 'info'];

function sortedMessages(messages: Message[]): Message[] {
    return [...messages].sort((a, b) => LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level));
}

function hasFieldError(row: Row, section: Section, key: string): boolean {
    return row.verification.messages[section].some((message) => message.level === 'error' && message.field === key);
}

function fullName(record: ImportRecord): string {
    return `${record.values.firstname ?? ''} ${record.values.lastname ?? ''}`.trim();
}

function isSelect(field: ImportField): boolean {
    return ['choice', 'yesNo'].includes(field.type);
}

// Countries and languages are lists of hundreds: as selects, every expanded row would render each
// option of each such field. A datalist is rendered once and only suggests while typing.
const SUGGESTED_TYPES = ['country', 'language'];

const datalists = computed(() => {
    const lists = new Map<string, string[]>();
    [...props.fields.child, ...props.fields.parent]
        .filter((field) => SUGGESTED_TYPES.includes(field.type))
        .forEach((field) => lists.set(datalistKey(field), field.choices));

    return Array.from(lists.entries(), ([id, choices]) => ({ id, choices }));
});

function datalistKey(field: ImportField): string {
    return `child-import-${field.type}`;
}

function datalistId(field: ImportField): string | undefined {
    return SUGGESTED_TYPES.includes(field.type) ? datalistKey(field) : undefined;
}

/** Keeps a value the list does not know selectable, so the user sees what the file says. */
function selectOptions(field: ImportField, value: string) {
    const choices = field.type === 'yesNo' ? YES_NO : field.choices;
    const options = choices.map((choice) => ({ value: choice, label: choice }));
    const known = choices.some((choice) => choice.toLowerCase() === value.toLowerCase());

    return value !== '' && !known ? [{ value, label: value }, ...options] : options;
}

function selectValue(field: ImportField, value: string): string {
    const choices = field.type === 'yesNo' ? YES_NO : field.choices;

    return choices.find((choice) => choice.toLowerCase() === value.toLowerCase()) ?? value;
}

/**
 * One pick per custodian, among what the verification offers: every parent of the kindergarten
 * sharing the name, the earlier row naming the custodian, a new parent, or leaving them out.
 */
function parentChoices(row: Row, slot: ParentSlot) {
    const { existingParents, earlierLine } = row.verification[slot];

    return [
        ...existingParents.map((parent) => ({ value: `existing:${parent.id}`, label: i18n.t('Verknüpfen mit {name}', { name: parent.label }) })),
        ...(earlierLine !== null ? [{ value: 'earlier', label: i18n.t('Bezugsperson aus Zeile {line} verwenden', { line: earlierLine }) }] : []),
        { value: 'new', label: i18n.t('Neu anlegen') },
        { value: 'excluded', label: i18n.t('Nicht importieren') },
    ];
}

function parentChoice(row: Row, slot: ParentSlot): string {
    const { choice, existingParentId } = row.verification[slot];

    return choice === 'existing' ? `existing:${existingParentId}` : choice;
}

function chooseParent(row: Row, slot: ParentSlot, value: unknown) {
    if (typeof value !== 'string' || value === '') {
        return;
    }

    const [choice, parentId] = value.split(':');
    const record = row[slot];
    record.choice = choice as ParentChoice;
    if (choice === 'existing') {
        record.existingParentId = Number(parentId);
    }

    // Shown right away; the verification that follows confirms or corrects it.
    const verification = row.verification[slot];
    verification.choice = record.choice;
    verification.existingParentId = choice === 'existing' ? record.existingParentId : null;
    verification.duplicateOfLine = choice === 'earlier' ? verification.earlierLine : null;

    changeSelection();
}

/** A slot the file left blank imports nothing, so there is nothing to pick until the user fills it in. */
function isParentEmpty(row: Row, slot: ParentSlot): boolean {
    return Object.values(row[slot].values).every((value) => value === '');
}

function isParentExcluded(row: Row, slot: ParentSlot): boolean {
    return row.verification[slot].choice === 'excluded';
}

function linksExisting(row: Row, slot: ParentSlot): boolean {
    return row.verification[slot].choice === 'existing';
}

/**
 * Only what the import takes from the row stays editable: of a linked custodian the fields describing
 * the link, of one already named in an earlier row everything but the person.
 */
function isParentFieldDisabled(row: Row, slot: ParentSlot, field: ImportField): boolean {
    return isParentExcluded(row, slot)
        || (linksExisting(row, slot) && !field.link)
        || (row.verification[slot].duplicateOfLine !== null && field.person);
}

function setValue(record: ImportRecord, key: string, value: unknown) {
    record.values[key] = value === null || value === undefined ? '' : String(value);
    markDirty();
}

function toggle(line: number) {
    const next = new Set(expanded.value);
    if (next.has(line)) {
        next.delete(line);
    } else {
        next.add(line);
    }

    expanded.value = next;
}

// Counts the changes, so a verification can tell that the rows moved on while it was running.
let revision = 0;
let reverifyQueued = false;

function markDirty() {
    dirty.value = true;
    revision++;
}

function onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    file.value = input.files?.[0] ?? null;
}

/**
 * Picking the same file again fires no change event, so the page would keep the File of the first
 * pick - and with it the file as it was then. Clearing the input makes every pick a fresh one.
 */
function resetFileInput() {
    file.value = null;
    if (fileInput.value !== null) {
        fileInput.value.value = '';
    }
}

function applyResponse(data: { rows: Row[] }) {
    rows.value = data.rows;
    dirty.value = false;
    // Keep open what the user opened, as long as the row is still there.
    expanded.value = new Set(rows.value.filter((row) => expanded.value.has(row.line)).map((row) => row.line));
}

function handleError(requestError: any) {
    const data = requestError?.response?.data;
    if (data?.rows) {
        applyResponse(data);
    }

    error.value = data?.error ?? i18n.t('Es ist ein Fehler aufgetreten.');
    missingColumns.value = data?.missingColumns ?? [];
}

function resetMessages() {
    error.value = '';
    missingColumns.value = [];
}

function upload() {
    if (file.value === null || pending.value) {
        return;
    }

    resetMessages();
    result.value = null;
    rows.value = [];
    // Line numbers mean other children in a new file, so nothing of the old one stays open.
    expanded.value = new Set();
    pending.value = true;
    const formData = new FormData();
    formData.append('file', file.value);

    axios.post(props.verifyUrl, formData)
        .then((response) => applyResponse(response.data))
        .catch(handleError)
        .finally(() => {
            pending.value = false;
            resetFileInput();
        });
}

function payload() {
    return {
        rows: rows.value.map(({ verification: _verification, ...row }) => row),
    };
}

function reverify() {
    if (pending.value) {
        return;
    }

    resetMessages();
    pending.value = true;
    const sentRevision = revision;
    axios.post(props.reverifyUrl, payload())
        .then((response) => {
            // The answer describes the rows as they were sent; applying it over a later change
            // would undo that change.
            if (sentRevision === revision) {
                applyResponse(response.data);
            }
        })
        .catch(handleError)
        .finally(() => {
            pending.value = false;
            if (reverifyQueued) {
                reverifyQueued = false;
                reverify();
            }
        });
}

/**
 * What gets imported decides which errors count and which custodians merge into which row, so a
 * changed selection is verified right away instead of leaving the old verdict on screen.
 */
function changeSelection() {
    markDirty();
    if (pending.value) {
        reverifyQueued = true;

        return;
    }

    reverify();
}

function commit() {
    if (!canImport.value) {
        return;
    }

    resetMessages();
    pending.value = true;
    axios.post(props.commitUrl, payload())
        .then((response) => {
            result.value = response.data.result;
            rows.value = [];
            const count = response.data.result.children.length;
            notify.success(count === 1
                ? i18n.t('Es wurde 1 Kind erfolgreich importiert.')
                : i18n.t('Es wurden {count} Kinder erfolgreich importiert.', { count }));
        })
        .catch(handleError)
        .finally(() => pending.value = false);
}

defineExpose({ rows, dirty, result, expanded, file, toggle, canImport, importAllState, toggleAll, changeSelection, chooseParent, parentChoice, parentChoices, isParentEmpty, upload, reverify, commit });
</script>

<template>
    <div class="flex flex-col gap-4">
        <h2 class="kgr-headline kgr-headline--h2">
            {{ title }}
        </h2>

        <div class="mb-4 flex max-w-4xl flex-col items-start gap-2">
            <p>
                {{ i18n.t('Mit dem Import legen Sie Kinder und ihre Bezugspersonen aus einer Excel-Datei an.') }}
                <template v-if="explanationOpen">
                    {{ i18n.t('Laden Sie die Vorlage herunter, füllen Sie eine Zeile je Kind aus und laden Sie die Datei hoch. Gespeichert wird erst, wenn Sie nach der Prüfung auf „Importieren“ klicken.') }}
                </template>
            </p>
            <ul
                v-if="explanationOpen"
                class="list-disc pl-5"
            >
                <li
                    v-for="hint in HINTS"
                    :key="hint"
                >
                    {{ i18n.t(hint) }}
                </li>
            </ul>
            <button
                type="button"
                class="kgr-link flex items-center gap-1"
                :aria-expanded="explanationOpen"
                data-test="toggle-explanation"
                @click="explanationOpen = !explanationOpen"
            >
                <SphIcon
                    :icon="explanationOpen ? ChevronUp : ChevronRight"
                    size="sm"
                />
                {{ i18n.t(explanationOpen ? 'Weniger anzeigen' : 'So funktioniert der Import') }}
            </button>
        </div>

        <template v-if="result">
            <SphCallout
                type="success"
                :title="i18n.t('Der Import wurde erfolgreich abgeschlossen.')"
            >
                <template #description>
                    {{ i18n.t('Importierte Kinder') }}: {{ result.children.length }} ·
                    {{ i18n.t('Neu angelegte Bezugspersonen') }}: {{ result.createdParents }} ·
                    {{ i18n.t('Verknüpfungen mit bestehenden Bezugspersonen') }}: {{ result.linkedParents }}
                </template>
                <template #actions>
                    <SphLink
                        :href="childrenUrl"
                        :label="i18n.t('Zur Kinderliste')"
                    />
                </template>
            </SphCallout>

            <section class="flex flex-col gap-2">
                <h3 class="kgr-headline kgr-headline--h4">
                    {{ i18n.t('Importierte Kinder') }} ({{ result.children.length }})
                </h3>
                <ul
                    class="import-result"
                    data-test="result"
                >
                    <li
                        v-for="child in result.children"
                        :key="child.id"
                    >
                        <a
                            :href="child.url"
                            class="import-result__child"
                        >
                            <span class="truncate">{{ child.name }}</span>
                            <SphIcon
                                :icon="ChevronRight"
                                size="sm"
                                class="import-result__icon"
                            />
                        </a>
                    </li>
                </ul>
            </section>
        </template>

        <SphCallout
            v-if="error"
            type="error"
            :title="error"
            :description="missingColumns.length > 0 ? `${i18n.t('Fehlende Spalten')}: ${missingColumns.join(', ')}` : ''"
        />

        <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
            <div class="kgr-postfix__fluid flex flex-wrap items-center gap-4">
                <input
                    ref="fileInput"
                    type="file"
                    accept=".xlsx"
                    class="hidden"
                    data-test="file"
                    @change="onFileChange"
                />
                <SphButton
                    :icon="FileSpreadsheet"
                    :label="i18n.t('Datei auswählen')"
                    :disabled="pending"
                    data-test="choose-file"
                    @click="fileInput?.click()"
                />
                <span
                    :class="file === null ? 'text-muted-foreground' : 'font-semibold'"
                    data-test="file-name"
                >{{ file?.name ?? i18n.t('Keine Datei ausgewählt') }}</span>
                <a
                    v-if="templateUrl"
                    :href="templateUrl"
                    class="kgr-link mx-auto flex items-center gap-1"
                >
                    <SphIcon
                        :icon="Download"
                        size="sm"
                    />
                    {{ i18n.t('Vorlage herunterladen') }}
                </a>
            </div>
            <div class="kgr-postfix__fixed">
                <SphButton
                    variant="primary"
                    :icon="Upload"
                    :label="i18n.t('Hochladen und prüfen')"
                    :disabled="file === null || pending"
                    @click="upload"
                />
            </div>
        </div>

        <template v-if="rows.length > 0">
            <datalist
                v-for="datalist in datalists"
                :id="datalist.id"
                :key="datalist.id"
            >
                <option
                    v-for="choice in datalist.choices"
                    :key="choice"
                    :value="choice"
                ></option>
            </datalist>

            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
                <div class="kgr-postfix__fluid flex flex-wrap gap-4">
                    <span>{{ i18n.t('Zeilen') }}: {{ rows.length }}</span>
                    <span>{{ i18n.t('Werden importiert') }}: {{ includedRows.length }}</span>
                    <span :class="{ 'text-destructive font-semibold': rowsWithErrors.length > 0 }">
                        {{ i18n.t('Fehler') }}: {{ rowsWithErrors.length }}
                    </span>
                    <span :class="{ 'text-warning-border font-semibold': rowsWithWarnings.length > 0 }">
                        {{ i18n.t('Warnungen') }}: {{ rowsWithWarnings.length }}
                    </span>
                    <label class="flex items-center gap-2">
                        <SphCheckbox v-model="onlyWithMessages" />
                        {{ i18n.t('Nur Fehler oder Warnungen anzeigen') }}
                    </label>
                </div>
                <div class="kgr-postfix__fixed flex gap-2">
                    <SphButton
                        :icon="RefreshCw"
                        :label="i18n.t('Erneut prüfen')"
                        :disabled="pending"
                        data-test="reverify"
                        @click="reverify"
                    />
                    <SphButton
                        variant="primary"
                        :label="i18n.t('Importieren')"
                        :disabled="!canImport"
                        data-test="commit"
                        @click="commit"
                    />
                </div>
            </div>

            <SphCallout
                v-if="dirty"
                type="warning"
                :title="i18n.t('Die Daten wurden geändert. Bitte prüfen Sie sie vor dem Import erneut.')"
            />

            <table class="kgr-table kgr-table--block kgr-table--fixed kgr-table--striped">
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline w-24 text-center">
                            {{ i18n.t('Zeile aus Excel') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline w-12"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline w-24 whitespace-nowrap">
                            {{ i18n.t('Status') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline w-32 whitespace-nowrap">
                            <label class="flex cursor-pointer items-center gap-2">
                                <SphCheckbox
                                    :model-value="importAllState"
                                    data-test="toggle-all"
                                    @update:model-value="toggleAll"
                                />
                                {{ i18n.t('Importieren') }}
                            </label>
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ i18n.t('Kind') }}
                        </th>
                        <th
                            v-for="{ slot, label } in PARENT_SLOTS"
                            :key="slot"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ i18n.t(label) }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template
                        v-for="row in visibleRows"
                        :key="row.line"
                    >
                        <tr
                            class="kgr-table__row"
                            :class="{ 'opacity-60': row.excluded }"
                            :data-test="`row-${row.line}`"
                        >
                            <td class="kgr-table__cell text-center">
                                {{ row.line }}
                            </td>
                            <td class="kgr-table__cell">
                                <SphButton
                                    :icon="expanded.has(row.line) ? ChevronDown : ChevronRight"
                                    variant="ghost"
                                    size="sm"
                                    :aria-label="i18n.t('Details')"
                                    @click="toggle(row.line)"
                                />
                            </td>
                            <td
                                class="kgr-table__cell whitespace-nowrap"
                                data-test="status"
                            >
                                <SphIcon
                                    v-if="row.excluded"
                                    :icon="CircleMinus"
                                    size="sm"
                                    class="text-muted-foreground"
                                    :title="i18n.t('Wird nicht importiert')"
                                />
                                <span
                                    v-else
                                    class="flex items-center gap-3"
                                >
                                    <span
                                        v-if="countByLevel(row, 'error') > 0"
                                        class="text-error-border flex items-center gap-1 font-semibold"
                                        :title="`${countByLevel(row, 'error')} ${i18n.t('Fehler')}`"
                                    >
                                        <SphIcon
                                            :icon="CircleX"
                                            size="sm"
                                        />
                                        {{ countByLevel(row, 'error') }}
                                    </span>
                                    <span
                                        v-if="countByLevel(row, 'warning') > 0"
                                        class="text-warning-border flex items-center gap-1 font-semibold"
                                        :title="`${countByLevel(row, 'warning')} ${i18n.t('Warnungen')}`"
                                    >
                                        <SphIcon
                                            :icon="TriangleAlert"
                                            size="sm"
                                        />
                                        {{ countByLevel(row, 'warning') }}
                                    </span>
                                    <SphIcon
                                        v-if="countByLevel(row, 'error') === 0 && countByLevel(row, 'warning') === 0"
                                        :icon="CircleCheck"
                                        size="sm"
                                        class="text-success-border"
                                        :title="i18n.t('OK')"
                                    />
                                </span>
                            </td>
                            <td class="kgr-table__cell">
                                <SphCheckbox
                                    :model-value="!row.excluded"
                                    @update:model-value="(value) => { row.excluded = value !== true; changeSelection(); }"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                {{ fullName(row.child) }}<br />
                                <small>{{ row.child.values.dateOfBirth }}</small>
                                <span
                                    v-if="row.verification.existingChildId"
                                    class="import-badge import-badge--warning ml-2"
                                >{{ i18n.t('existiert bereits') }}</span>
                            </td>
                            <td
                                v-for="{ slot } in PARENT_SLOTS"
                                :key="slot"
                                class="kgr-table__cell"
                                :class="{ 'line-through': isParentExcluded(row, slot) }"
                            >
                                {{ fullName(row[slot]) }}
                                <span
                                    v-if="linksExisting(row, slot)"
                                    class="import-badge import-badge--info ml-2"
                                >{{ i18n.t('bestehend') }}</span>
                                <span
                                    v-else-if="row.verification[slot].duplicateOfLine"
                                    class="import-badge ml-2"
                                >{{ i18n.t('Bezugsperson aus Zeile {line}', { line: row.verification[slot].duplicateOfLine }) }}</span>
                            </td>
                        </tr>
                        <tr
                            v-if="expanded.has(row.line)"
                            class="kgr-table__row"
                        >
                            <td
                                class="kgr-table__cell import-details"
                                colspan="7"
                            >
                                <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                    <section class="flex flex-col gap-2">
                                        <h4 class="kgr-headline kgr-headline--h4">
                                            {{ i18n.t('Kind') }}
                                        </h4>
                                        <label
                                            v-if="row.verification.existingChildId"
                                            class="flex items-center gap-2"
                                        >
                                            <SphCheckbox
                                                :model-value="!row.excluded"
                                                data-test="import-existing-child"
                                                @update:model-value="(value) => { row.excluded = value !== true; changeSelection(); }"
                                            />
                                            {{ i18n.t('Trotzdem als neues Kind importieren') }}
                                        </label>
                                        <ul class="flex flex-col gap-1">
                                            <li
                                                v-for="(message, index) in sortedMessages(row.verification.messages.child)"
                                                :key="index"
                                                :class="`import-message import-message--${message.level}`"
                                            >
                                                <SphIcon
                                                    :icon="MESSAGE_ICONS[message.level]"
                                                    size="sm"
                                                    class="import-message__icon"
                                                />
                                                {{ message.text }}
                                            </li>
                                        </ul>
                                        <SphField
                                            v-for="field in fields.child"
                                            :key="field.key"
                                            :label="i18n.t(field.label)"
                                            :required="field.required"
                                        >
                                            <SphSelect
                                                v-if="isSelect(field)"
                                                :model-value="selectValue(field, row.child.values[field.key] ?? '')"
                                                :options="selectOptions(field, row.child.values[field.key] ?? '')"
                                                :invalid="hasFieldError(row, 'child', field.key)"
                                                clearable
                                                size="sm"
                                                @update:model-value="(value) => setValue(row.child, field.key, value)"
                                            />
                                            <SphInput
                                                v-else
                                                :list="datalistId(field)"
                                                :model-value="row.child.values[field.key] ?? ''"
                                                :invalid="hasFieldError(row, 'child', field.key)"
                                                size="sm"
                                                @update:model-value="(value) => setValue(row.child, field.key, value)"
                                            />
                                        </SphField>
                                    </section>

                                    <section
                                        v-for="{ slot, label } in PARENT_SLOTS"
                                        :key="slot"
                                        class="flex flex-col gap-2"
                                    >
                                        <h4 class="kgr-headline kgr-headline--h4">
                                            {{ i18n.t(label) }}
                                        </h4>
                                        <SphField
                                            v-if="!isParentEmpty(row, slot)"
                                            :label="i18n.t('Import der Bezugsperson')"
                                        >
                                            <SphSelect
                                                :model-value="parentChoice(row, slot)"
                                                :options="parentChoices(row, slot)"
                                                :data-test="`choice-${slot}`"
                                                :null-option="false"
                                                size="sm"
                                                @update:model-value="(value) => chooseParent(row, slot, value)"
                                            />
                                        </SphField>
                                        <ul class="flex flex-col gap-1">
                                            <li
                                                v-for="(message, index) in sortedMessages(row.verification.messages[slot])"
                                                :key="index"
                                                :class="`import-message import-message--${message.level}`"
                                            >
                                                <SphIcon
                                                    :icon="MESSAGE_ICONS[message.level]"
                                                    size="sm"
                                                    class="import-message__icon"
                                                />
                                                {{ message.text }}
                                            </li>
                                        </ul>
                                        <SphField
                                            v-for="field in fields.parent"
                                            :key="field.key"
                                            :label="i18n.t(field.label)"
                                        >
                                            <SphSelect
                                                v-if="isSelect(field)"
                                                :model-value="selectValue(field, row[slot].values[field.key] ?? '')"
                                                :options="selectOptions(field, row[slot].values[field.key] ?? '')"
                                                :invalid="hasFieldError(row, slot, field.key)"
                                                :disabled="isParentFieldDisabled(row, slot, field)"
                                                clearable
                                                size="sm"
                                                @update:model-value="(value) => setValue(row[slot], field.key, value)"
                                            />
                                            <SphInput
                                                v-else
                                                :list="datalistId(field)"
                                                :model-value="row[slot].values[field.key] ?? ''"
                                                :invalid="hasFieldError(row, slot, field.key)"
                                                :disabled="isParentFieldDisabled(row, slot, field)"
                                                size="sm"
                                                @update:model-value="(value) => setValue(row[slot], field.key, value)"
                                            />
                                        </SphField>
                                    </section>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </template>
    </div>
</template>

<style scoped>
.kgr-table__cell.import-details {
    padding-bottom: 2rem;
}

.import-badge {
    display: inline-block;
    padding: 0 0.375rem;
    border: 1px solid var(--color-gray-400);
    border-radius: var(--radius-sm, 0.25rem);
    background-color: var(--color-background);
    color: var(--color-foreground);
    font-size: 0.75rem;
    line-height: 1.25rem;
    white-space: nowrap;
}

.import-badge--warning {
    border-color: var(--color-warning-border);
    background-color: color-mix(in srgb, var(--color-warning) 40%, white);
    color: var(--color-warning-foreground);
}

.import-badge--info {
    border-color: var(--color-info-border);
    background-color: color-mix(in srgb, var(--color-info) 30%, white);
    color: var(--color-info-foreground);
}

.import-result {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 0.5rem;
}

.import-result__child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-md, 0.375rem);
    background-color: var(--color-background);
    color: var(--color-foreground);
    text-decoration: none;
}

.import-result__child:hover {
    border-color: var(--color-gray-400);
    background-color: var(--color-gray-50);
}

.import-result__icon {
    flex-shrink: 0;
    color: var(--color-gray-400);
}

.import-message {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem;
}

.import-message__icon {
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: var(--color-gray-400);
}

.import-message--error {
    background-color: color-mix(in srgb, var(--color-error) 15%, transparent);
    color: var(--color-error-border);
}

.import-message--error .import-message__icon {
    color: var(--color-error-border);
}

.import-message--warning {
    background-color: color-mix(in srgb, var(--color-warning) 25%, transparent);
}

.import-message--warning .import-message__icon {
    color: var(--color-warning-border);
}
</style>
